import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import MessageModel from "../../../../domain/model/message_model";
import { MessageFrom } from "../../../../domain/enum/message_from";
import { MessageStatus } from "../../../../domain/enum/message_status";
import { chatActions, chatRepository } from "../../../di/di";
import { VoiceCallStatus } from "../../../../domain/enum/voice_call_status";
import { DateUtils } from "../../../../../core/utils/date_utils";
import { PromptType } from "../../../../domain/enum/prompt_type";
import Toast from "../../../components/popups/toast";
import { VideoCallStatus } from "../../../../domain/enum/video_call_status";
import { AIStatus } from "../../../../domain/enum/ai_status";
import { ErrorType } from "../../../../domain/enum/error_type";
import { AIMode } from "../../../../domain/enum/ai_mode";
import { galleryActions } from "../../../di/di";
import { getAvatarProfile } from "../../../state/slice/model_profile_slice";
import { StringUtils } from "../../../../../core/utils/string_utils";
import TrackingService from "../../../../data/service/impl/tracking_service_impl";

const trackingService = new TrackingService();

export const ChatUIAction = Object.freeze({
  initial: "Initial",
  showVoiceCallErrorMessage: "showVoiceCallErrorMessage",
  showVoiceCallInfoMessage: "showVoiceCallInfoMessage",
});

export const ChatPrevMsgStatus = Object.freeze({
  none: "None",
  hasPrevData: "HasPrevData",
  getOngoing: "GetOngoing",
  getFailed: "GetFailed",
  topReached: "TopReached",
});

const initialState = {
  isInitDone: false,
  isInitOngoing: false,
  isFailedInit: false,
  isScrollToBottom: false,
  isTyping: false,
  isGeneratingImage: false,
  errorMessage: "",
  showCall: false,
  showVideoCall: false,
  videoCallUrl: "",
  enteredMessage: "",
  messageToSend: "",
  chatData: {},
  messageList: [],
  hasPendingMessageProcess: false, // This is used to prevent multiple message sends
  action: ChatUIAction.initial,
  showGallery: false,
  videoCallData: {},
  showSubscriptionModal: false,
  voiceCallStatus: VoiceCallStatus.initiating,
  voiceCallTime: "00:00",
  currentStage: -1,
  performFocusToIndex: false,
  focusIndex: 0,
  chatPrevMsgStatus: ChatPrevMsgStatus.none,
  aiMode: AIMode.listening,
  showClearChatModal: false,
  isClearingChat: false,
  seenMessageKeys: {},
  currentVideoCallUsage: 0,
  didPerformVideoCall: false,
};

let voiceCallInterval = null; // Declare interval variable outside the function

const startVoiceCallTime = (dispatch) => {
  const startTime = Date.now(); // Capture the start time
  voiceCallInterval = setInterval(() => {
    const elapsedTime = Date.now() - startTime;
    const minutes = String(Math.floor(elapsedTime / 60000)).padStart(2, "0");
    const seconds = String(Math.floor((elapsedTime % 60000) / 1000)).padStart(2, "0");
    dispatch(chatActions.setVoiceCallTime(`${minutes}:${seconds}`));
  }, 1000);
};

const stopVoiceCallTime = () => {
  clearInterval(voiceCallInterval);
};

export const selectIsVideoCallOngoing = (state) => state.chat.showVideoCall;

export const createChatSlice = (chatRepository) => {
  return createSlice({
    name: "chat",
    initialState,
    reducers: {
      resetState: () => initialState,
      setUIAction(state, action) {
        const data = action.payload;
        state.action = data;
      },
      setIsScrollToBottom(state, action) {
        const data = action.payload;
        state.isScrollToBottom = data;
      },
      setEnteredMessage(state, action) {
        const data = action.payload;
        state.enteredMessage = data;
      },
      setIsInitDone(state, action) {
        const data = action.payload;
        state.isInitDone = data;
      },
      setShowCall(state, action) {
        const data = action.payload;
        state.showCall = data;
      },
      setShowVideoCall(state, action) {
        const data = action.payload;
        state.showVideoCall = data;
      },
      setShowGallery(state, action) {
        const data = action.payload;
        state.showGallery = data;
      },
      setVideoCallData(state, action) {
        const data = action.payload;
        state.videoCallData = data;
        console.log("videoCallData", state.videoCallData);
      },
      closeVideoCall(state, action) {
        state.showVideoCall = false;
        state.videoCallData = {};
        state.currentVideoCallUsage = 0;
        chatRepository.endVideoCall(state.chatData.id);
        state.currentStage = -1;

        // Force cleanup of media streams for Safari/iOS
        if (typeof window !== "undefined" && navigator.mediaDevices) {
          // This is a workaround for Safari/iOS to ensure camera/mic indicators are removed
          navigator.mediaDevices
            .getUserMedia({ video: true, audio: true })
            .then((stream) => {
              // Stop all tracks to release camera/mic
              stream.getTracks().forEach((track) => {
                track.stop();
              });
            })
            .catch((err) => {
              console.log("Media cleanup error:", err);
            });
        }

        // Additional cleanup for Safari/iOS
        if (typeof window !== "undefined") {
          // Force reload getUserMedia to reset permissions
          setTimeout(() => {
            if (navigator.mediaDevices) {
              navigator.mediaDevices
                .getUserMedia({ video: false, audio: false })
                .then((stream) => {
                  stream.getTracks().forEach((track) => track.stop());
                })
                .catch(() => {
                  // Ignore errors here
                });
            }
          }, 100);
        }
      },
      resetVideoCallData: (state, action) => {
        state.videoCallData = {};
      },
      closeVoiceCall(state, action) {
        const performEnd = action.payload;
        state.showCall = false;
        state.voiceCallData = {};
        state.voiceCallStatus = VoiceCallStatus.initiating;
        state.voiceCallTime = "00:00";
        stopVoiceCallTime();
        if (performEnd) {
          chatRepository.endVoiceCall();
        }
      },
      setShowSubscriptionModal(state, action) {
        const data = action.payload;
        state.showSubscriptionModal = data;
      },
      setVoiceCallStatus(state, action) {
        const data = action.payload;
        state.voiceCallStatus = data;
        console.log("Voice Call Status", state.voiceCallStatus);
      },
      setVoiceCallTime(state, action) {
        const data = action.payload;
        state.voiceCallTime = data;
      },
      messagingInitOngoing(state, action) {
        state.isInitOngoing = true;
        state.isFailedInit = false;
      },
      messagingInitSuccess(state, action) {
        state.isInitOngoing = false;
        state.isInitDone = true;
        state.isScrollToBottom = true;
        state.chatData = action.payload.chatData;
        state.messageList = action.payload.messagesData;
        state.chatPrevMsgStatus = state.messageList.length < 50 ? ChatPrevMsgStatus.none : ChatPrevMsgStatus.hasPrevData;
        console.log("Message Init success", state.chatPrevMsgStatus);
      },
      messagingInitFailed(state, action) {
        state.isInitOngoing = false;
        state.isInitDone = false;
        state.isFailedInit = true;
      },
      messageSend(state, action) {
        const data = action.payload;

        const chatChannelId = state.chatData.id;

        // Generate Message Unique Key
        const now = new Date();
        const dateString = now
          .toISOString()
          .replace(/[-:TZ.]/g, "")
          .slice(2, 14);
        const uniqueKey = `${chatChannelId}_${dateString}_${StringUtils.generateRandomCharacters()}`;

        // Populate Message to List
        state.messageList = [
          ...state.messageList,
          new MessageModel({
            message: state.enteredMessage,
            from: MessageFrom.user,
            status: MessageStatus.sending,
            uniqueKey: uniqueKey,
            dateString: DateUtils.formatDate(new Date()),
          }),
        ];

        state.messageToSend = state.enteredMessage;
        state.enteredMessage = "";
        state.isScrollToBottom = true;
        state.hasPendingMessageProcess = true;

        // Send Message to Server
        chatRepository.sendMessage({ message: state.messageToSend, uniqueKey: uniqueKey });
      },
      appendAIResponse(state, action) {
        state.messageList = [...state.messageList, action.payload];
        state.isScrollToBottom = true;
      },
      showPrompt(state, action) {
        const data = action.payload;
        state.errorMessage = data.message;
        if (data.promptType === PromptType.info) {
          state.action = ChatUIAction.showVoiceCallInfoMessage;
        } else {
          state.action = ChatUIAction.showVoiceCallErrorMessage;
        }
      },
      setPerformFocusToIndex(state, action) {
        const data = action.payload;
        state.performFocusToIndex = data;
      },
      setIsTyping(state, action) {
        const data = action.payload;
        state.isTyping = data;
      },
      setIsGeneratingImage(state, action) {
        const data = action.payload;
        state.isGeneratingImage = data;
      },
      setCurrentStage(state, action) {
        state.currentStage = action.payload;
      },
      setAIMode(state, action) {
        state.aiMode = action.payload;
      },
      setShowClearChatModal(state, action) {
        const data = action.payload;
        state.showClearChatModal = data;
      },
      setIsClearingChat(state, action) {
        const data = action.payload;
        state.isClearingChat = data;
      },
      clearChat(state) {
        state.messageList = [];
        state.chatData = {};
        state.enteredMessage = "";
        state.messageToSend = "";
        state.showClearChatModal = false;
        state.isClearingChat = false;
      },
      setVideoCallUrl(state, action) {
        const data = action.payload;
        state.videoCallUrl = data;
      },
      setSeenMessageKeys(state, action) {
        const data = action.payload;
        const updatedSeenMessageKeys = { ...state.seenMessageKeys };
        updatedSeenMessageKeys[data.uniqueKey] = data.status;
        state.seenMessageKeys = updatedSeenMessageKeys;
      },
      incrementVideoCallUsage(state, action) {
        state.currentVideoCallUsage += 1;
      },
      setHasPendingMessageProcess(state, action) {
        state.hasPendingMessageProcess = action.payload;
      },
      setDidPerformVideoCall(state, action) {
        state.didPerformVideoCall = action.payload;
      },
    },
    extraReducers: (builder) => {
      builder
        .addCase(getPreviousMessages.pending, (state) => {
          state.chatPrevMsgStatus = ChatPrevMsgStatus.getOngoing;
        })
        .addCase(getPreviousMessages.fulfilled, (state, action) => {
          const prevMessagesList = action.payload;
          state.messageList = [...prevMessagesList, ...state.messageList];
          state.performFocusToIndex = true;
          state.focusIndex = prevMessagesList.length - 1;
          state.chatPrevMsgStatus = prevMessagesList.length < 50 ? ChatPrevMsgStatus.topReached : ChatPrevMsgStatus.hasPrevData;
          console.log("Get Previous Messages Fulfilled", state.chatPrevMsgStatus);
        })
        .addCase(getPreviousMessages.rejected, (state, action) => {
          const status = action.payload?.status ?? ChatPrevMsgStatus.none;
          console.log("REJECTED", status);
          if (status === ChatPrevMsgStatus.getFailed) {
            state.chatPrevMsgStatus = status;
          }
        });
    },
  });
};

export const getPreviousMessages = createAsyncThunk("chat/getPreviousMessages", async (data, { getState, rejectWithValue, extra }) => {
  const state = getState().chat;
  const chatRepository = extra.chatRepository;

  const chatChannelId = state.chatData.id;
  const currentMessagesCount = state.messageList.length;

  console.log("Get Previous Messages", chatChannelId, currentMessagesCount);

  const result = await chatRepository.getPreviousMessages({
    chatChannelId: chatChannelId,
    currentMessagesCount: currentMessagesCount,
  });
  if (result.success) {
    return result.data;
  } else {
    return rejectWithValue({ status: ChatPrevMsgStatus.getFailed });
  }
});

export const startMessaging = (dispatch, store) => {
  let state = store.getState().chat;
  let avatarProfileState = store.getState().modelProfile.profile;

  dispatch(chatActions.messagingInitOngoing());
  chatRepository.startMessaging({
    resultCallback: (data) => {
      if (data.success) {
        console.log("Messaging Init Success", data);
        dispatch(chatActions.messagingInitSuccess(data));

        // Get the Avatar Details
        dispatch(getAvatarProfile({ avatarId: data.chatData.avatarId }));
      } else {
        dispatch(chatActions.messagingInitFailed());
      }
    },
    errorCallback: (error) => {
      // Set has pending message process to false
      dispatch(chatActions.setHasPendingMessageProcess(false));
    },
    noCreditsCallback: () => {
      // Show subscription modal
      dispatch(chatActions.setShowSubscriptionModal(true));
      // Set has pending message process to false
      dispatch(chatActions.setHasPendingMessageProcess(false));
    },
    statusCallback: (status) => {},
    aiStatusCallback: (status) => {
      dispatch(chatActions.setIsTyping(status === AIStatus.typing));
      dispatch(chatActions.setIsGeneratingImage(status === AIStatus.generatingImage));
    },
    aiResponseCallback: (response) => {
      // Append AI Response to chat box
      dispatch(chatActions.appendAIResponse(response));
      // Append AI Images to gallery
      dispatch(galleryActions.appendImages(response.images));
      // Set has pending message process to false
      dispatch(chatActions.setHasPendingMessageProcess(false));
    },
    messageStatusCallback: (uniqueKey, status) => {
      console.log("Message Status", uniqueKey, status);
      dispatch(chatActions.setSeenMessageKeys({ uniqueKey: uniqueKey, status: status }));
    },
  });
};

export const startVideoCall = (dispatch, store) => {
  let state = store.getState().chat;
  let userState = store.getState().profile.user;

  // Check if user has enough credits
  // if (userState.videoCallUsage <= 0) {
  //   // Trigger the USR_NO_VCC_CREDITS error
  //   const error = {
  //     type: ErrorType.USR_NO_VCC_CREDITS,
  //     message: "You have no video call credits remaining. Please upgrade your plan to continue.",
  //   };
  //   Toast.error(error.message);
  //   dispatch(chatActions.setShowVideoCall(false));

  //   // Only show subscription modal if user is not yet subscribed
  //   if (!userState.tierId) {
  //     dispatch(chatActions.setShowSubscriptionModal(true));
  //   }

  //   return;
  // }

  // This is used to prevent always calling of getUser() details upon going back or selecting profile tab.
  // getUser() and getActiveTiers() is called if ever the user perform a video call.
  dispatch(chatActions.setDidPerformVideoCall(true));

  dispatch(chatActions.setShowVideoCall(true));

  const chatChannelId = state.chatData.id;
  chatRepository.startVideoCall({
    chatChannelId,
    videoCallStatusCB: (status, data = {}) => {
      switch (status) {
        case VideoCallStatus.ongoing:
          dispatch(chatActions.setCurrentStage(1));
          dispatch(chatActions.setVideoCallData(data));
          // Increment usage counter
          dispatch(chatActions.incrementVideoCallUsage());
          trackingService.trackEvent("video_call_started", { userId: userState.id, distinct_id: userState.id, timestamp: new Date().toISOString() });
          break;
        case VideoCallStatus.ended:
          dispatch(chatActions.resetVideoCallData());
          break;
        default:
          console.log(`Unknown status '${status}'`);
          if(status !== VideoCallStatus.initiating){
            trackingService.trackEvent("video_call_failed", { userId: userState.id, distinct_id: userState.id, failure_reason: status, timestamp: new Date().toISOString() });
          }
      }
    },
    noCreditsCB: (error) => {
      dispatch(chatActions.setCurrentStage(0));
      dispatch(chatActions.resetVideoCallData());
      Toast.error(error.message);
      dispatch(chatActions.setShowVideoCall(false));

      // Only show subscription modal if user is not yet subscribed
      if (!userState.tierId) {
        dispatch(chatActions.setShowSubscriptionModal(true));
        trackingService.trackEvent("video_call_failed", { userId: userState.id, distinct_id: userState.id, failure_reason: ErrorType.USR_NO_VCC_CREDITS, timestamp: new Date().toISOString() });
      }
    },
    errorCB: (error) => {
      // Handle VDC_IN_PROGRESS error specifically
      if (error.type === ErrorType.VDC_IN_PROGRESS) {
        Toast.error(error.message);
        dispatch(chatActions.closeVideoCall());
        dispatch(chatActions.setShowVideoCall(false));
        trackingService.trackEvent("video_call_failed", { userId: userState.id, distinct_id: userState.id, failure_reason: ErrorType.VDC_IN_PROGRESS, timestamp: new Date().toISOString() });
      }
      // Handle USR_NO_VCC_CREDITS error specifically
      else if (error.type === ErrorType.USR_NO_VCC_CREDITS) {
        Toast.error(error.message);
        dispatch(chatActions.setShowVideoCall(false));

        // Only show subscription modal if user is not yet subscribed
        if (!userState.tierId) {
          dispatch(chatActions.setShowSubscriptionModal(true));
          trackingService.trackEvent("video_call_failed", { userId: userState.id, distinct_id: userState.id, failure_reason: ErrorType.USR_NO_VCC_CREDITS, timestamp: new Date().toISOString() });
        }
      }
      // Handle USR_NOT_SUBSCRIBED error specifically
      else if (error.type === ErrorType.USR_NOT_SUBSCRIBED) {
        Toast.error("You currently do not have an active subscription. Please subscribe to access all available features.");
        dispatch(chatActions.closeVideoCall());
        dispatch(chatActions.setShowVideoCall(false));
        dispatch(chatActions.setShowSubscriptionModal(true));
        trackingService.trackEvent("video_call_failed", { userId: userState.id, distinct_id: userState.id, failure_reason: ErrorType.USR_NOT_SUBSCRIBED, timestamp: new Date().toISOString() });
      }
      // Handle other errors
      else {
        dispatch(chatActions.setCurrentStage(0));
        Toast.error(error.message);
        trackingService.trackEvent("video_call_failed", { userId: userState.id, distinct_id: userState.id, failure_reason: error.type, timestamp: new Date().toISOString() });
      }
    },
  });
};

export const startVoiceCall = async (dispatch, store) => {
  let state = store.getState().chat;
  dispatch(chatActions.setShowCall(true));
  dispatch(chatActions.setVoiceCallTime("00:00"));
  console.log(store.getState().profile.user, 'user na ngani');
  const result = await chatRepository.startVoiceCall({
    chatChannelId: state.chatData.id,
    user: store.getState().profile.user,
    callStatusCallback: (status) => {
      console.log("CALL STATUS CALL BACK RECEIVED", status);
      if (status === VoiceCallStatus.ongoing) {
        startVoiceCallTime(dispatch); // Start the voice call timer that you can see during the call.
      } else if (status === VoiceCallStatus.ended) {
        dispatch(chatActions.closeVoiceCall()); // The function for stopping the timer is inside here.
      }
      dispatch(chatActions.setVoiceCallStatus(status));
    },
    noCreditsCallback: () => {
      dispatch(chatActions.setShowSubscriptionModal(true));
    },
    aiModeCallback: (mode) => {
      console.log("Mode Callback", mode);
      dispatch(chatActions.setAIMode(mode));
    },
    errorCallback: (error) => {
      console.log("voiceCallError", error);
      Toast.error(error.message);
      //console.log("CHAT SLICE ERROR RECEIVED", error);
      //dispatch(chatActions.showPrompt(error));
    },
  });

  if (!result.success) {
    Toast.warning(result.message);
    dispatch(chatActions.closeVoiceCall());
  }
};

export const clearChatMessages = (dispatch, store, { chatChannelId, resultCallback, errorCallback }) => {
  dispatch(chatActions.setIsClearingChat(true));

  chatRepository.clearChatMessages({
    chatChannelId,
    resultCallback: (result) => {
      dispatch(chatActions.setIsClearingChat(false));
      resultCallback(result);
    },
    errorCallback: (error) => {
      dispatch(chatActions.setIsClearingChat(false));
      errorCallback(error);
    },
  });
};
